import { useEffect, useRef } from 'react'

export interface SsoHandoffProps {
  /** The BFF login-entry URL to hand off to. A full-page-navigation target
   * (never a `fetch()` call) — see the callers' own doc comments for why
   * (C22). */
  readonly loginUrl: string
  readonly heading?: string
  readonly message?: string
  readonly linkLabel?: string
}

/**
 * Shared "we're sending you to your IdP" screen, used by both
 * `<LoginRedirectPage>` (single global OIDC provider, S0.3) and
 * `<CompanySsoPage>` (per-account enterprise SSO, S0.5) — the two callers
 * differ only in HOW they arrive at a `loginUrl`, not in how the handoff
 * itself behaves or is presented.
 *
 * Auto-redirects on mount via a full-page navigation, but ALSO renders a
 * manual "Continue" link per the Gate 2 UX spec's `<SSORedirectBanner>`
 * note: "auto-redirect can fail silently in some browsers — always give a
 * manual affordance" (WCAG 2.2 avoids relying on auto-timing alone).
 *
 * Also moves focus to its own heading on mount: unlike a route change (which
 * `<AppShell>` handles by focusing `<main>`), a step transition INSIDE a
 * page (e.g. `<CompanySsoPage>`'s email step -> redirecting step) doesn't
 * trigger that, so this component announces itself the same way `<LoginPage>`
 * does for its own step transitions (USP-42 pattern).
 */
export function SsoHandoff({
  loginUrl,
  heading = 'Signing you in',
  message = 'Redirecting you to sign in…',
  linkLabel = 'Continue to sign in',
}: SsoHandoffProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    window.location.href = loginUrl
    // Depending on `loginUrl` (not an empty array): if the resolved login
    // target changes while this component is mounted, the redirect should
    // carry the current value, not whatever was present at first mount.
    // Re-navigating is idempotent — each assignment is just another
    // redirect hand-off to the BFF.
  }, [loginUrl])

  useEffect(() => {
    headingRef.current?.focus()
    // Mount-only: this component is always freshly mounted for a given
    // handoff (a route entry or a step change), so there is no "loginUrl
    // changed but we're still the same screen" case that would need focus
    // re-stolen.
  }, [])

  return (
    <div className="login-redirect">
      <h1 ref={headingRef} tabIndex={-1}>
        {heading}
      </h1>
      <output aria-live="polite">{message}</output>
      <p>
        <a href={loginUrl}>{linkLabel}</a>
      </p>
    </div>
  )
}
